import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/common/DashboardLayout';
import * as hubAnnouncementService from '../../services/hubAnnouncement.service';

export default function AnnouncementArchive() {
  const navigate = useNavigate();
  const [announcements, setAnnouncements] = useState(null);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null); // null = not searching
  const [searching, setSearching] = useState(false);

  const load = useCallback(async () => {
    try {
      setError(null);
      setAnnouncements(await hubAnnouncementService.getAnnouncementArchive());
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load the archive.');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submitSearch = async (e) => {
    e.preventDefault();
    if (!query.trim()) { setSearchResults(null); return; }
    setSearching(true);
    try {
      setSearchResults(await hubAnnouncementService.searchAnnouncements(query.trim()));
    } finally {
      setSearching(false);
    }
  };

  const clearSearch = () => { setQuery(''); setSearchResults(null); };

  const visible = searchResults !== null ? searchResults : announcements;

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto">
        <button onClick={() => navigate('/dashboard/discussion')} className="text-xs text-gray-400 hover:text-reset-brown mb-4">
          ← My Hive
        </button>

        <h1 className="font-serif text-2xl font-bold text-reset-brown mb-1">HMMMM? Archive</h1>
        <p className="text-sm text-gray-500 mb-6">Every thought-provoking prompt from the reset team, in one place.</p>

        <form onSubmit={submitSearch} className="mb-6 flex gap-2">
          <input
            type="search"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search by topic…"
            className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
          />
          <button
            type="submit"
            className="px-4 py-2 bg-reset-terracotta text-white text-sm font-medium rounded-lg hover:bg-reset-brown transition-colors"
          >
            Search
          </button>
          {searchResults !== null && (
            <button type="button" onClick={clearSearch} className="px-3 py-2 text-sm text-gray-500 hover:text-reset-brown">
              Clear
            </button>
          )}
        </form>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm">{error}</div>
        )}

        {searchResults !== null && (
          <p className="text-xs text-gray-400 mb-3">
            {searching ? 'Searching…' : `${searchResults.length} result${searchResults.length === 1 ? '' : 's'} for "${query}"`}
          </p>
        )}

        {!visible ? (
          <p className="text-sm text-gray-400">Loading…</p>
        ) : visible.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm px-5 py-8 text-center text-sm text-gray-400">
            {searchResults !== null ? 'No results match that search.' : 'Nothing here yet.'}
          </div>
        ) : (
          <div className="space-y-3">
            {visible.map(a => (
              <button
                key={a.id}
                onClick={() => navigate(`/dashboard/announcements/${a.id}`)}
                className="flex gap-3 group text-left w-full bg-white rounded-2xl shadow-sm p-4"
              >
                <div className="w-14 h-14 rounded-lg bg-reset-sage/10 shrink-0 overflow-hidden flex items-center justify-center text-xl">
                  {a.image_url ? <img src={a.image_url} alt="" className="w-full h-full object-cover" /> : '📣'}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-reset-brown leading-snug group-hover:underline">
                    {a.title || a.message_text}
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {new Date(a.created_at).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })}
                  </p>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
